import { useForm } from 'react-hook-form';
import { FormProvider, SelectTag, Table, TextField } from '../../../../../components';
import {
  Button,
  Container,
  Dialog,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  TextField as MuiTextField
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { dropdownFetch, getKeyPathValue, request, toast } from '../../../../../utils';
import CloseIcon from '@mui/icons-material/Close';
import { RECEIPT_MODE, BLOOD_BANK_TYPE } from '../../../../../constants';
import { useLocation } from 'react-router-dom';
import { CustomButtonn } from '../../../../../components';
import { IntakeBloodDetailsForm } from './IntakeBloodDetailsForm';

const BILL_TABLE_HEADER = [
  {
    header: 'Component Name',
    accessor: (row) => row?.component_name || row?.component?.component
  },
  {
    header: 'No. Of Unit',
    accessor: (row)=> row?.purchase_details?.quantity
  },
  {
    header: 'Blood Group',
    accessor: (row) => row?.blood_group_name || row?.blood_group?.group
  },
  {
    header: 'Amount',
    accessor: (row)=> row?.purchase_details?.amount
  }
];

const TABLE_HEADER = [
  {
    header: 'Blood Intake No',
    accessor: (row) => row?.blood_intake_no
  },
  {
    header: 'Associate Blood Bank/Donor',
    accessor: (row) => row?.associate_blood_bank?.blood_bank_name || row?.donor?.patient_name
  },
  {
    header: 'Blood Type',
    accessor: (row) => row?.blood_type?.blood_type
  },
  {
    header: 'Status',
    accessor: 'status',
    sx: { textTransform: 'uppercase' }
  }
];

const initialBloodIntakeDataObj = {
  data: [],
  loading: false
};

export const IntakeBlood = () => {
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();
  const pathParts = pathname.split('/');
  const organisation = pathParts[1];
  const [addDetailsDialogOpen, setAddDetailsDialogOpen] = useState(false);
  const [detailList, setDetailList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [intakeBloodDataObj, setIntakeBloodDataObj] = useState(initialBloodIntakeDataObj);
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const [originalData, setOriginalData] = useState([]);
  const [input, setInput] = useState('');
  const [bloodBankDropdown, setBloodBankDropdown] = useState([]);
  const [bloodTypeDropdown, setBloodTypeDropdown] = useState([]);
  const [bloodGroupDropdown, setBloodGroupDropdown] = useState([]);
  const [componentDropdown, setComponentDropdown] = useState([]);
  const [donorDropdown, setDonorDropdown] = useState([]);
  const user = JSON.parse(localStorage.getItem('user'));

  const isEnabledDate =
    user && user.permissions ? user.permissions.some((permission) => permission.includes('DatePermission')) : false;

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        // blood_type: Yup.string().required(' ')
      })
    ),
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      time: new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5),
      bill: {
        total_qty: 0,
        total_amt: 0,
        net_amt: 0
      }
    },
    mode: 'all'
  });
  const { handleSubmit, watch, reset, setValue } = methods;

  useEffect(() => {
    let total_qty = 0;
    let total_amt = 0;

    detailList &&
      detailList.forEach((detail) => {
        total_qty += detail.purchase_details.quantity;
        total_amt += detail.purchase_details.amount;
      });

    setValue('bill.total_qty', total_qty);
    setValue('bill.total_amt', total_amt);
    setValue('bill.net_amt', total_amt);
  }, [detailList, setValue]);

  const netAmtWatch = watch('bill.net_amt');
  const recAmt1Watch = watch('bill.rec_amt_1');
  const recAmt2Watch = watch('bill.rec_amt_2');
  const payMode = watch('bill.pay_mode');

  useEffect(() => {
    const total_recpt = +recAmt1Watch + +recAmt2Watch;
    let balanceAmt = netAmtWatch - total_recpt;

    if (payMode === 'CASH') {
      setValue('bill.rec_amt_1', netAmtWatch); // Set the total net amount to rec_amt_1
      setValue('bill.rec_amt_2', 0); // Clear rec_amt_2
      setValue('bill.balance_amt', 0); // Set balance amount to 0
      setValue('bill.total_recpt', netAmtWatch); // Set total receipt to the net amount
    } else if (['Bank', 'UPI', 'Paytm', 'RTGS/NEFT/Cheque', 'Credit Card', 'Debit Card'].includes(payMode)) {
      setValue('bill.rec_amt_1', 0); // Clear rec_amt_1 for non-cash payment modes
      setValue('bill.rec_amt_2', netAmtWatch); // Set the total net amount to rec_amt_2
      setValue('bill.balance_amt', 0); // Set balance amount to 0
      setValue('bill.total_recpt', netAmtWatch); // Set total receipt to the net amount
    } else if (payMode === 'Credit') {
      // For Credit mode, calculate and set balance amount
      setValue('bill.rec_amt_1', 0); // Clear rec_amt_1 for credit payment
      setValue('bill.rec_amt_2', 0); // Clear rec_amt_2 for credit payment
      setValue('bill.balance_amt', balanceAmt); // Set balance amount to remaining balance
      setValue('bill.total_recpt', 0); // Set total receipt to 0 since no payment is made
    } else {
      // Default case when no payment mode is matched
      setValue('bill.total_recpt', total_recpt);
      setValue('bill.balance_amt', balanceAmt);
    }
  }, [netAmtWatch, recAmt1Watch, recAmt2Watch, payMode, setValue]);
  useEffect(() => {
    if (selectedRecord) {
      const keyValuePair = getKeyPathValue(selectedRecord, ['__v', '_id', 'createdAt', 'status', 'blood_items']);
      keyValuePair.forEach(({ key, value }) => setValue(key, value));
      setValue('associate_blood_bank', selectedRecord.associate_blood_bank?._id);
      setValue('blood_type', selectedRecord.blood_type?._id);
      setValue('blood_bank_type', selectedRecord.blood_bank_type);
      setValue('donor', selectedRecord.donor?._id);
      setValue('bill.pay_mode', selectedRecord.bill?.pay_mode);
      const formattedbillDate = selectedRecord?.date ? new Date(selectedRecord.date).toISOString().split('T')[0] : '';
      setValue('date', formattedbillDate);
      setValue('time', selectedRecord?.billing_time);
      setDetailList(selectedRecord.blood_items);
    } else {
      reset();
    }
  }, [selectedRecord]);

  const getBloodGroupDropdown = useCallback(async () => {
    const data = await dropdownFetch('blood-bank-group');
    setBloodGroupDropdown(data);
  }, []);

  const getBloodTypeDropdown = useCallback(async () => {
    const data = await dropdownFetch('blood-type');
    setBloodTypeDropdown(data);
  }, []);

  const getBloodBankDropdown = useCallback(async () => {
    const data = await dropdownFetch('blood-bank-master');
    setBloodBankDropdown(data);
  }, []);

  const getComponentDropdown = useCallback(async () => {
    const data = await dropdownFetch('component');
    setComponentDropdown(data);
  }, []);

  const getDonorDropdown = useCallback(async () => {
    const data = await dropdownFetch('donor');
    setDonorDropdown(data);
  }, []);

  useEffect(() => {
    getBloodGroupDropdown();
    getBloodTypeDropdown();
    getBloodBankDropdown();
    getComponentDropdown();
    getDonorDropdown();
  }, [getBloodGroupDropdown, getBloodTypeDropdown, getBloodBankDropdown, getComponentDropdown, getDonorDropdown]);

  const formSubmit = async (formValues) => {
    if (detailList.length === 0) {
      toast('Please add the Details First', { variant: 'warning' });
      return;
    }
    setLoading(true);
    const payload = { ...formValues, blood_items: detailList };
    let editFlag = false;
    if (selectedRecord && currentAction === 'edit') {
      editFlag = true;
    }
    const response = await request(editFlag ? '/blood-intake-edit' : '/blood-intake-create', {
      method: editFlag ? 'PUT' : 'POST',
      params: editFlag ? selectedRecord._id : null,
      body: {
        ...payload,
        createdBy: user.name,
        updatedBy: editFlag ? user.name : '',
        organisation
      }
    });
    if (response?.success) {
      toast(`Successfully ${editFlag ? 'edited' : 'created'} Blood Intake`, { variant: 'success' });
      setSelectedRecord(response.data);
      setCurrentAction();
      setDetailList([]);
      reset();
    } else {
      toast(`Failed to ${editFlag ? 'edit' : 'create'} Blood Intake`, { variant: 'error' });
    }
    setLoading(false);
  };

  const handleRowDelete = (rowId) => {
    setDetailList((prev) =>
      prev.filter((data) => {
        return data.component !== rowId
      })
    );
  };

  const onDetailAddHandler = (data) => {
      toast('Details Added Successfully', { variant: 'success' });
    const componentName = componentDropdown.find(({ _id }) => _id === data.component)?.component;
    const bloodGroupName = bloodGroupDropdown.find(({ _id }) => _id === data.blood_group)?.group;
    setDetailList((prev) => [
      ...prev,
      {
        ...data,
        component_name: componentName,
        blood_group_name: bloodGroupName
      }
    ]);
  };

  const handleCancelRecord = async () => {
    setLoading(true);
    const response = await request('/blood-intake-cancel', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully cancel Blood Intake', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to cancel Blood Intake', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleDeleteRecord = async () => {
    setLoading(true);
    const response = await request('/blood-intake-delete', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully deleted Blood Intake', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to delete Blood Intake', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleQueryWindow = async () => {
    setOpenQueryDialog(true);
    setIntakeBloodDataObj({ loading: true });
    const response = await request('/blood-intake-list', { method: 'GET', query: { limit: 10 } });
    if (response.success) {
      const sortedData = response.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      const filteredData = response.data.filter(
        (item) => item.status === 'active' || item.status === 'inactive' || item.status === 'default'
      );
      setIntakeBloodDataObj({ loading: false, data: filteredData });
      setOriginalData(sortedData);
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setIntakeBloodDataObj({ loading: false });
    }
  };

  const handleChange = (e) => {
    const newInput = e.target.value;
    setInput(newInput);
  };

  const handleSearchData = async () => {
    const lowercasedInput = input.toLowerCase().trim();
    setIntakeBloodDataObj({ loading: true, data: [] });
    try {
      const response = await request('/blood-intake-list', {
        method: 'GET',
        query: { name: lowercasedInput, limit: 10 }
      });
      if (response.success) {
        const filteredData = response.data.filter(
          (item) => item.status === 'active' || item.status === 'inactive' || item.status === 'default'
        );
        setIntakeBloodDataObj({ loading: false, data: filteredData });
      } else {
        toast('Error in fetching data', { variant: 'error' });
        setIntakeBloodDataObj({ loading: false, data: [] });
      }
    } catch (error) {
      console.error('Error during search:', error);
      toast('Error during search', { variant: 'error' });
      setIntakeBloodDataObj({ loading: false, data: [] });
    }
  };
  useEffect(() => {
    handleSearchData(input);
  }, [input]);

  const watchBloodBankType = watch('blood_bank_type');



  return (
    <>
      <Paper
        sx={{
          p: 0,
          flex: 1,
          borderRadius: 0,
          boxShadow: 'none',
          width: '100%',
          height: 'auto'
        }}
      >
        <Container
          sx={{
            mt: 2,
            p: 0.2,
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: '100vw',
            boxSizing: 'border-box',
            minWidth: '100%'
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                pb: 0.5,
                mb: 3,
                borderBottom: '1px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                Blood Intake
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={`${lastPathSegment}Write`}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => {
                    setSelectedRecord();
                    setCurrentAction('new');
                    setDetailList([]);
                    reset();
                  }}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Update`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={() => setCurrentAction('edit')}
                >
                  Edit
                </CustomButtonn>
                <CustomButtonn
                  id={'Submit'}
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                >
                  Save
                </CustomButtonn>
                <CustomButtonn
                  id={'Cancel'}
                  variant="contained"
                  disabled={!selectedRecord || currentAction}
                  onClick={() => {
                    setCurrentAction();
                    handleCancelRecord();
                  }}
                >
                  Cancel
                </CustomButtonn>
                <CustomButtonn
                  id={`isSuperAdmin`}
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                  onClick={handleDeleteRecord}
                >
                  Delete
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Read`}
                  variant="contained"
                  onClick={handleQueryWindow}
                  disabled={['new', 'edit'].includes(currentAction)}
                >
                  Query
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Print`}
                  variant="contained"
                  onClick={() => window.open(`/print${pathname}?refId=${selectedRecord._id}`, '_blank')}
                  disabled={!selectedRecord || currentAction}
                >
                  Print
                </CustomButtonn>
                <CustomButtonn
                  id={'Exit'}
                  variant="contained"
                  onClick={() => {
                    setCurrentAction('');
                  }}
                >
                  Exit
                </CustomButtonn>
              </Stack>
            </Stack>
            <Grid
              container
              spacing={2}
              sx={{
                boxShadow: '0px 0px 10px 3px #0099ff',
                borderRadius: '10px',
                padding: '10px',
                height: '55vh',
                border: '1px solid grey'
              }}
            >
              <Grid item xs={8} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Stack
                  spacing={2}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ width: '100%' }}
                >
                  <Stack spacing={2} direction="row" alignItems="center" sx={{ width: '100%' }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '1.4rem',
                        flex: '0 0 30%', // Adjusted for better space distribution
                        color: 'text.primary'
                      }}
                    >
                      Bank Type
                    </Typography>
                    <SelectTag
                      menus={BLOOD_BANK_TYPE}
                      disabled={!['new', 'edit'].includes(currentAction)}
                      formControlSX={{ flex: 1 }}
                      placeholder="Select Blood Bank Type"
                      name="blood_bank_type"
                    />
                  </Stack>

                  {/* Blood Bank or Donor */}
                  {watchBloodBankType === 'blood bank' ? 
                    <Stack spacing={2} direction="row" alignItems="center" sx={{ width: '100%' }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: '1.4rem',
                          flex: '0 0 30%', // Adjusted for better space distribution
                          color: 'text.primary'
                        }}
                      >
                        Associated Blood Bank
                      </Typography>
                      <SelectTag
                        menus={bloodBankDropdown.map((data) => ({ ...data, name: data.blood_bank_name }))}
                        disabled={!['new', 'edit'].includes(currentAction)}
                        formControlSX={{ flex: 1 }}
                        placeholder=""
                        name="associate_blood_bank"
                      />
                    </Stack>
                   :
                    <Stack spacing={2} direction="row" alignItems="center" sx={{ width: '100%' }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: '1.4rem',
                          flex: '0 0 30%', // Adjusted for better space distribution
                          color: 'text.primary'
                        }}
                      >
                        Donor Name
                      </Typography>
                      <SelectTag
                        menus={donorDropdown.map((data) => ({ ...data, name: data.patient_name }))}
                        disabled={!['new', 'edit'].includes(currentAction)}
                        formControlSX={{ flex: 1 }}
                        placeholder=""
                        name="donor"
                      />
                    </Stack>
                  }
                  {/* Add Details Button */}
                  <Stack direction="row" alignItems="center" sx={{ width: 'auto' }}>
                    <Button
                      variant="contained"
                      disabled={!['new', 'edit'].includes(currentAction)}
                      onClick={() => setAddDetailsDialogOpen(true)}
                      sx={{
                        backgroundColor: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'primary.dark'
                        }
                      }}
                    >
                      Add Details
                    </Button>
                  </Stack>
                </Stack>

                <Table
                  containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                  data={detailList?.map((data) => ({ _id: data.component, ...data })) || []}
                  columns={BILL_TABLE_HEADER}
                  size="small"
                  showDeleteAction={true}
                  onDeleteHandler={handleRowDelete}
                />
              </Grid>
           {watchBloodBankType === "blood bank" ? (<Grid item xs={4} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Pay Mode</Typography>
                  <SelectTag
                    disabled={!['new', 'edit'].includes(currentAction)}
                    formControlSX={{
                      flex: 0.8,
                      border: currentAction ? '0.5px solid red' : '',
                      backgroundColor: currentAction ? '#B3E5FC' : ''
                    }}
                    name="bill.pay_mode"
                    placeholder="Pay Mode"
                    menus={RECEIPT_MODE}
                    onChange={() => setValue('bank_cash', '')}
                  />
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Auth Code</Typography>
                  <TextField
                    disabled={!['new', 'edit'].includes(currentAction)}
                    sx={{ flex: 0.8 }}
                    name="bill.auth_code"
                    placeholder="Auth Code"
                  />
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Total Qty/Amt</Typography>
                  <TextField disabled sx={{ flex: 0.3 }} name="bill.total_qty" placeholder="Qty" />
                  <TextField disabled sx={{ flex: 0.5 }} name="bill.total_amt" placeholder="Amt" />
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Disc./TDS Amt</Typography>
                  <TextField disabled sx={{ flex: 0.4 }} name="bill.disc_amt" placeholder="Discount" />
                  <TextField disabled sx={{ flex: 0.4 }} name="bill.tds_amt" placeholder="TDS Amount" />
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Net Amt</Typography>
                  <TextField disabled sx={{ flex: 0.8 }} name="bill.net_amt" placeholder="Net Amount" />
                </Stack>
              </Grid>):<Grid item xs={4} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}></Grid>}
            </Grid>
          </FormProvider>
        </Container>
      </Paper>

      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '80%', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            Blood Intake Query Window
          </Typography>
          <IconButton onClick={() => setOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 0 }}>
          <MuiTextField
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '0',
                fontSize: '1.4rem'
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 1.4rem'
              },
              '& .MuiInputLabel-root': {
                fontSize: '1.2rem'
              }
            }}
            label="Search Patient"
            variant="outlined"
            value={input}
            onChange={handleChange}
          />
        </Stack>
        <Table
          sx={{ mt: 0.5 }}
          columns={TABLE_HEADER}
          data={intakeBloodDataObj?.data || []}
          loading={intakeBloodDataObj.loading}
          onRowClick={(row) => {
            delete row.patient;
            setSelectedRecord(row);
            setOpenQueryDialog(false);
          }}
        />
      </Dialog>
      <Dialog
        open={addDetailsDialogOpen}
        onClose={() => setAddDetailsDialogOpen(false)}
        PaperProps={{ sx: { width: '50%', height: '55vh', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <IntakeBloodDetailsForm
          onClose={() => setAddDetailsDialogOpen(false)}
          onAdd={onDetailAddHandler}
          bloodGroupDropdown={bloodGroupDropdown}
          componentDropdown={componentDropdown}
        />
      </Dialog>
    </>
  );
};
